/**
 * Closing, funding and fees (docs/PLAN.md §4 PR 14, PRD J6, AC15/AC16/AC17).
 *
 * Closing starts from the buyer's selected offer and is a list of *tasks* — the partner's
 * closing conditions and the paperwork around them — each with an owner, a due date, and,
 * once done, the evidence and the Tex verifier who checked it. Readiness is derived on every
 * read: the selected offer must still be the partner's current word and still open; an offer
 * that expires or is replaced while the case is in `closing` invalidates readiness with a
 * stated reason, and the case has to go back through offer review.
 *
 * Funding is what the *counterparties* confirm, never what Tex does. Tex holds no money and
 * executes no disbursement instruction; it records the partner's confirmation that it funded
 * (amount, date, reference, payee, evidence) and — separately — the seller's confirmation that
 * the money arrived. Only the partner's evidenced confirmation moves a case to `funded`; a
 * signed document, an approval or a checkbox never does (AC16).
 *
 * The fee ledger is append-only. `expected_fee` is what the partner agreement says Tex is owed;
 * `fee_collected` is a receipt Tex can independently evidence. Nothing is ever inferred from a
 * funding amount (AC17): until a receipt is recorded the collected total is zero, however large
 * the funding.
 */

import type { CaseStage, MoneyView } from './api-types'
import type { OfferView } from './offers'

// ── closing tasks ────────────────────────────────────────────────────────────

export const CLOSING_TASK_OWNERS = ['borrower', 'dealer', 'tex', 'partner'] as const
export type ClosingTaskOwner = (typeof CLOSING_TASK_OWNERS)[number]

export const CLOSING_TASK_STATUSES = ['open', 'blocked', 'complete', 'waived'] as const
export type ClosingTaskStatus = (typeof CLOSING_TASK_STATUSES)[number]

/** Statuses that count as "resolved" for funding purposes. */
export const RESOLVED_TASK_STATUSES: readonly ClosingTaskStatus[] = ['complete', 'waived']

export interface ClosingTaskView {
  id: string
  case_id: string
  offer_id: string | null
  position: number
  title: string
  description: string | null
  owner: ClosingTaskOwner
  status: ClosingTaskStatus
  due_on: string | null
  /** The document that proves the task was done. Only set on `complete`. Tex and the owning audience see it. */
  evidence_document_id: string | null
  /** When the task was completed or waived, and by which Tex user (the verifier). */
  completed_at: string | null
  completed_by_user_id: string | null
  /** Why it was waived / blocked / reopened, as typed by the verifier. */
  note: string | null
  created_at: string
  updated_at: string
}

export interface ClosingTaskSummary {
  open: number
  blocked: number
  complete: number
  waived: number
}

export interface NewClosingTask {
  title: string
  description?: string
  owner: ClosingTaskOwner
  due_on?: string
}

export interface DealerClosingTaskText {
  title: string
  description?: string
  note?: string
}

export interface TexClosingTaskView extends ClosingTaskView {
  dealer_text: {
    title: string
    description: string | null
    note: string | null
    approved_by_user_id: string
    approved_at: string
  } | null
}

/** `POST /cases/:id/closing/tasks/:taskId/approve-dealer-text`; null revokes approval. */
export interface ApproveDealerTaskTextBody {
  expected_updated_at: string
  dealer_text: DealerClosingTaskText | null
}

/** `POST /cases/:id/closing/tasks` */
export interface AddClosingTaskBody {
  task: NewClosingTask
}

/** `POST /cases/:id/closing/tasks/:taskId/complete` — evidence is mandatory; the caller is the verifier. */
export interface CompleteClosingTaskBody {
  evidence_document_id: string
  note?: string
}

/** `POST /cases/:id/closing/tasks/:taskId/waive|block|reopen` — a reason is mandatory. */
export interface ClosingTaskNoteBody {
  note: string
}

// ── readiness ────────────────────────────────────────────────────────────────

/**
 * Where the case stands on the way from a selected offer to money moving:
 *   `not_started`   before offer review, or in offer review with nothing (valid) selected
 *   `ready_to_close` in offer review with an open, presentable offer selected — `enterClosing` will succeed
 *   `closing`       in closing on a still-valid selected offer; tasks may be open
 *   `invalidated`   in closing but the selected offer is no longer open / presentable — nothing may proceed
 *   `funded`        the partner's evidenced funding confirmation has been recorded
 *   `closed`        terminal, whatever the reason
 */
export const CLOSING_STATUSES = ['not_started', 'ready_to_close', 'closing', 'invalidated', 'funded', 'closed'] as const
export type ClosingStatus = (typeof CLOSING_STATUSES)[number]

export type ClosingBlocker =
  | { kind: 'invalid_stage'; stage: CaseStage }
  | { kind: 'no_selected_offer' }
  | { kind: 'selected_offer_expired'; offer_id: string; expires_at: string | null }
  | { kind: 'selected_offer_withdrawn'; offer_id: string }
  | { kind: 'selected_offer_superseded'; offer_id: string }
  | { kind: 'selected_offer_not_presentable'; offer_id: string; covered_state: string | null }
  | { kind: 'closing_tasks_open'; task_ids: string[] }
  | { kind: 'funding_not_confirmed' }

export interface ClosingReadinessView {
  status: ClosingStatus
  /** Why the case cannot enter closing / be funded right now; empty when `status` is `ready_to_close`, `closing` (all tasks resolved) or `funded`. */
  blockers: ClosingBlocker[]
  /** True when the selected offer stopped being valid *after* closing began (AC15): the buyer must re-select once the partner's new word is recorded. */
  reevaluation_required: boolean
  /** One-line, audience-safe explanation of `reevaluation_required`. */
  reason: string | null
}

// ── funding ──────────────────────────────────────────────────────────────────

export const FUNDING_EVENT_KINDS = ['partner_confirmed_funding', 'dealer_confirmed_receipt', 'correction', 'reversal'] as const
export type FundingEventKind = (typeof FUNDING_EVENT_KINDS)[number]

/** Who the partner says it paid. `other` needs a note. */
export const PAYEE_CATEGORIES = ['seller', 'borrower', 'other'] as const
export type PayeeCategory = (typeof PAYEE_CATEGORIES)[number]

export interface FundingEventView {
  id: string
  case_id: string
  offer_id: string | null
  partner_id: string
  /** Null in the dealer projection: the seller sees the money moved, not which partner (AC13). */
  partner_display_name: string | null
  kind: FundingEventKind
  /** Signed: a reversal is negative. */
  amount: MoneyView
  funded_on: string | null
  external_reference: string | null
  payee_category: PayeeCategory | null
  evidence_document_id: string | null
  corrects_event_id: string | null
  note: string | null
  synthetic: boolean
  recorded_by_user_id: string
  recorded_at: string
}

export interface FundingSummaryView {
  /** Sum of `partner_confirmed_funding` rows and the corrections/reversals that point at them. */
  net_partner_funded: MoneyView
  /** Sum of `dealer_confirmed_receipt` rows and the corrections/reversals that point at them. */
  net_dealer_received: MoneyView
  partner_confirmed: boolean
  dealer_receipt_confirmed: boolean
  first_funded_on: string | null
}

/** Tex + dealer: every event. The dealer is the seller and sees what was confirmed to have moved. */
export interface FundingResponse {
  audience: 'tex' | 'dealer'
  case_id: string
  case_version: number
  summary: FundingSummaryView
  events: FundingEventView[]
}

/**
 * `POST /cases/:id/funding-events`. Exactly one shape per kind so the server never has to guess
 * what a missing field means. The partner's confirmation *is* the `closing → funded` transition
 * and must carry its evidence (AC16).
 */
export type RecordFundingEventBody =
  | {
      kind: 'partner_confirmed_funding'
      expected_version: number
      amount: MoneyView
      funded_on: string
      payee_category: PayeeCategory
      evidence_document_id: string
      external_reference?: string
      note?: string
    }
  | {
      kind: 'dealer_confirmed_receipt'
      amount: MoneyView
      funded_on: string
      evidence_document_id: string
      external_reference?: string
      note?: string
    }
  | {
      kind: 'correction' | 'reversal'
      corrects_event_id: string
      /** Signed delta for a correction, which must leave the corrected event's net positive; a reversal must be the negative of that net (amount plus corrections). A reversed event takes no further adjustment. */
      amount: MoneyView
      note: string
      evidence_document_id?: string
      external_reference?: string
    }

export interface RecordFundingEventResponse {
  event: FundingEventView
  case_stage: CaseStage
  case_version: number
  summary: FundingSummaryView
}

// ── fees ─────────────────────────────────────────────────────────────────────

export const FEE_ENTRY_TYPES = ['expected_fee', 'fee_collected', 'correction'] as const
export type FeeEntryType = (typeof FEE_ENTRY_TYPES)[number]

export interface FeeEntryView {
  id: string
  case_id: string
  dealer_id: string
  partner_id: string
  funding_event_id: string | null
  entry_type: FeeEntryType
  amount: MoneyView
  basis: string | null
  external_reference: string | null
  evidence_document_id: string | null
  corrects_entry_id: string | null
  /** For a correction: the type of the entry at the root of the chain it adjusts. */
  adjusts: Exclude<FeeEntryType, 'correction'> | null
  note: string | null
  synthetic: boolean
  recorded_by_user_id: string
  recorded_at: string
}

export interface FeeSummaryView {
  expected: MoneyView
  /** Zero until a receipt is recorded with its own evidence — never derived from funding (AC17). */
  collected: MoneyView
  outstanding: MoneyView
}

/** Tex ops only. */
export interface FeeLedgerResponse {
  case_id: string
  summary: FeeSummaryView
  entries: FeeEntryView[]
}

export type RecordFeeEntryBody =
  | {
      entry_type: 'expected_fee'
      amount: MoneyView
      /** Which agreement clause / rate produced the number — text, not a formula. */
      basis: string
      funding_event_id?: string
      note?: string
    }
  | {
      entry_type: 'fee_collected'
      amount: MoneyView
      /** Independent proof of receipt (remittance advice, bank confirmation). Mandatory. */
      evidence_document_id: string
      external_reference?: string
      funding_event_id?: string
      note?: string
    }
  | {
      entry_type: 'correction'
      corrects_entry_id: string
      /** Signed delta. */
      amount: MoneyView
      note: string
      evidence_document_id?: string
    }

export interface RecordFeeEntryResponse {
  entry: FeeEntryView
  summary: FeeSummaryView
}

// ── the closing view, per audience ───────────────────────────────────────────

/** `POST /cases/:id/closing` — named command `enterClosing`. Optional initial tasks are created atomically. */
export interface EnterClosingBody {
  expected_version: number
  tasks?: NewClosingTask[]
  note?: string
}

interface ClosingViewBase {
  case_id: string
  stage: CaseStage
  case_version: number
  readiness: ClosingReadinessView
  task_summary: ClosingTaskSummary
  funding: Pick<FundingSummaryView, 'partner_confirmed' | 'dealer_receipt_confirmed' | 'first_funded_on'>
}

/** Tex: everything, including the selected offer's terms and the conditions it came with. */
export interface TexClosingView extends ClosingViewBase {
  audience: 'tex'
  selected_offer: OfferView | null
  tasks: TexClosingTaskView[]
}

/** The buyer: their own tasks with evidence, everyone else's title/owner/status, the selected offer. */
export interface BorrowerClosingView extends ClosingViewBase {
  audience: 'borrower'
  selected_offer: OfferView | null
  tasks: ClosingTaskView[]
}

/** The dealer: explicitly approved own-task text with evidence, neutral labels otherwise. No offer terms or partner identity (AC13). */
export interface DealerClosingView extends ClosingViewBase {
  audience: 'dealer'
  tasks: ClosingTaskView[]
}

export type ClosingView = TexClosingView | BorrowerClosingView | DealerClosingView

export const CLOSING_REASONS: Record<Exclude<ClosingBlocker['kind'], 'invalid_stage' | 'closing_tasks_open' | 'funding_not_confirmed'>, string> = {
  no_selected_offer: 'No offer is selected; the buyer must choose one before closing can proceed.',
  selected_offer_expired: 'The selected offer has expired; the partner must re-issue terms and the buyer must re-select.',
  selected_offer_withdrawn: 'The partner withdrew the selected offer; the buyer must re-select once new terms arrive.',
  selected_offer_superseded: 'The partner changed the terms of the selected offer; the buyer must review and re-select.',
  selected_offer_not_presentable: 'The selected offer is waiting on the provider disclosure and cannot proceed.',
}
